import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { Body, Button, Row, Stack } from '@leanin/ui';

import {
  useCreateBroadcast,
  type CreateBroadcastInput,
} from '@/lib/mutations/admin';
import {
  useCreateGroupBroadcast,
  type CreateGroupBroadcastInput,
} from '@/lib/mutations/groups';
import { errorMessage } from '@/lib/error-message';
import { useStepUp } from '@/lib/use-step-up';
import type { BroadcastDoc } from '@/lib/queries/broadcasts';
import { emptyBroadcastDoc } from './BroadcastBodyEditor';
import {
  BroadcastEditorFields,
  audienceSpecFromState,
  EMPTY_AUDIENCE_STATE,
  type AudienceState,
  type BroadcastAudienceContext,
} from './BroadcastEditorFields';
import {
  PLATFORM_EMAIL_SEND_VIA,
  composeContentFromDraft,
  contentDraftGap,
  contentDraftHasInput,
  newContentDraft,
  type BroadcastContentDraft,
} from './broadcast-content-draft';
import { ScheduleSendButton } from './ScheduleSendButton';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

// One-time prefill, applied on mount. Either variant makes the
// unsaved-changes guard consider the form dirty immediately — deliberate:
// navigating away would lose the prefill.
export type BroadcastComposerInitial =
  // Group mode, from a source post ("Send as broadcast"): seeds the subject
  // and body; `sourcePostId` rides the create request so the sent broadcast
  // links back to the post.
  | {
      readonly kind: 'fromPost';
      readonly subject: string;
      readonly bodyDoc: BroadcastDoc;
      readonly sourcePostId: string;
    }
  // Platform mode, from the /admin/members bulk "Send email" action: seeds
  // the audience (a hand-picked `members` list).
  | { readonly kind: 'audience'; readonly audience: AudienceState };

export interface BroadcastComposerFormProps {
  readonly audience: BroadcastAudienceContext;
  readonly initial?: BroadcastComposerInitial;
}

// `submit()` throws this for locally-constructed, already human-readable
// failures (client-side validation, a step-up outcome already resolved by
// `runStepUp`/`failureFromError`) — as opposed to a raw `DomainError` bubbling
// up from `createGroup.mutateAsync` (group-audience sends aren't step-up'd),
// whose `.message` is a machine code. The catch blocks below tell the two
// apart instead of rendering `err.message` for both.
class ComposerMessageError extends Error {}

// The composer's three intents. `scheduledIso` rides on 'schedule' alone so a
// schedule can't be dispatched without a time and a send/draft can't carry one.
type ComposerAction =
  | { readonly mode: 'send' | 'draft' }
  | { readonly mode: 'schedule'; readonly scheduledIso: string };

export function BroadcastComposerForm({
  audience,
  initial,
}: BroadcastComposerFormProps) {
  const navigate = useNavigate();
  const createPlatform = useCreateBroadcast();
  const createGroup = useCreateGroupBroadcast();
  const { runStepUp } = useStepUp();

  const tDm = useTranslations('Admin.broadcasts.dm');
  const fromPost = initial?.kind === 'fromPost' ? initial : null;
  const [content, setContent] = useState<BroadcastContentDraft>(() =>
    newContentDraft({
      subject: fromPost?.subject ?? '',
      preheaderText: '',
      bodyDoc: fromPost?.bodyDoc ?? emptyBroadcastDoc(),
      replyTo: '',
    }),
  );
  const [audienceState, setAudienceState] = useState<AudienceState>(
    initial?.kind === 'audience' ? initial.audience : EMPTY_AUDIENCE_STATE,
  );
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [savePending, setSavePending] = useState(false);

  const pending = createPlatform.isPending || createGroup.isPending;

  // Any audience edit (every change yields a new state object) or prefill is
  // a choice the admin would lose by navigating away.
  const dirty =
    contentDraftHasInput(content) ||
    (audience.mode === 'platform' && audienceState !== EMPTY_AUDIENCE_STATE);

  const guard = useUnsavedChangesGuard({ dirty });

  const gap = contentDraftGap(content);
  const minimumFieldsForSave = gap === null;

  // Returns true when the action completed, false when the user dismissed
  // the step-up reverification prompt. Validation and hard failures throw.
  const submit = async (action: ComposerAction): Promise<boolean> => {
    setError(null);
    if (gap === 'subject') {
      throw new ComposerMessageError('Subject is required.');
    }
    if (gap === 'message') {
      throw new ComposerMessageError('Message is required.');
    }

    if (audience.mode === 'platform') {
      const fields = {
        audienceSpec: audienceSpecFromState(audienceState),
        content: composeContentFromDraft(content, PLATFORM_EMAIL_SEND_VIA),
      };
      const input: CreateBroadcastInput =
        action.mode === 'schedule'
          ? { ...fields, mode: 'schedule', scheduledSendAt: action.scheduledIso }
          : { ...fields, mode: action.mode };
      // Committing a platform-wide send is step-up'd server-side (RBAC
      // Decision C). runStepUp opens Clerk's reverification modal on
      // `admin/requires_step_up` and retries once on success; it passes
      // through transparently for draft saves, which carry no step-up.
      const outcome = await runStepUp(
        () => createPlatform.mutateAsync(input),
        'Failed to send broadcast.',
      );
      if (outcome.status === 'cancelled') return false;
      if (outcome.status === 'error') throw new ComposerMessageError(outcome.message);
      return true;
    }

    // Group broadcasts are email; the Channel control is platform-only.
    const { email } = content;
    const preheaderValue = email.preheaderText.trim();
    const replyToValue = email.replyTo.trim();
    const fields = {
      kind: audience.kind,
      groupId: audience.groupId,
      subject: email.subject.trim(),
      bodyDoc: email.bodyDoc,
      ...(fromPost !== null ? { sourcePostId: fromPost.sourcePostId } : {}),
      ...(preheaderValue.length > 0 ? { preheaderText: preheaderValue } : {}),
      ...(replyToValue.length > 0 ? { replyTo: replyToValue } : {}),
    };
    const input: CreateGroupBroadcastInput =
      action.mode === 'schedule'
        ? { ...fields, mode: 'schedule', scheduledSendAt: action.scheduledIso }
        : { ...fields, mode: action.mode };
    await createGroup.mutateAsync(input);
    return true;
  };

  const navigateAway = () => {
    guard.allowNextNavigation();
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  const handleSendNow = async () => {
    try {
      if (await submit({ mode: 'send' })) navigateAway();
    } catch (err) {
      setError(
        err instanceof ComposerMessageError
          ? err.message
          : errorMessage(err, 'Failed to send.'),
      );
    }
  };

  const handleSchedule = async (iso: string) => {
    try {
      if (await submit({ mode: 'schedule', scheduledIso: iso })) navigateAway();
    } catch (err) {
      setError(
        err instanceof ComposerMessageError
          ? err.message
          : errorMessage(err, 'Failed to schedule.'),
      );
    }
  };

  const handleGuardSave = async () => {
    setDialogError(null);
    setSavePending(true);
    try {
      const completed = await submit({ mode: 'draft' });
      setSavePending(false);
      if (completed) guard.proceed();
    } catch (err) {
      setSavePending(false);
      setDialogError(
        err instanceof ComposerMessageError
          ? err.message
          : errorMessage(err, 'Failed to save.'),
      );
    }
  };

  const handleCancel = () => {
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  return (
    <Stack gap="lg">
      <BroadcastEditorFields
        audience={audience}
        content={content}
        onContentChange={setContent}
        audienceState={audienceState}
        onAudienceStateChange={setAudienceState}
        disabled={pending}
      />

      {error !== null ? (
        <Body size="sm" color="destructive">
          {error}
        </Body>
      ) : null}

      <Row gap="md" justify="flex-end" align="center">
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <ScheduleSendButton
          disabled={!minimumFieldsForSave}
          pending={pending}
          onSendNow={handleSendNow}
          onSchedule={handleSchedule}
        />
      </Row>

      <UnsavedChangesDialog
        open={guard.dialogOpen}
        onCancel={guard.reset}
        onSave={() => void handleGuardSave()}
        onDiscard={guard.proceed}
        saveDisabled={!minimumFieldsForSave}
        saveDisabledReason={
          minimumFieldsForSave
            ? undefined
            : content.channel === 'dm'
              ? tDm('saveDisabledReason')
              : 'Subject and message are required to save as draft.'
        }
        savePending={savePending}
        error={dialogError}
      />
    </Stack>
  );
}

